import React, { createContext, useContext, useState, useEffect } from "react";
import { apiService } from "../services/api";
import toast from "react-hot-toast";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  const handleTokenExpiry = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("requires_password_change");
    setUser(null);
    setRequiresPasswordChange(false);
  };

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    const userData = localStorage.getItem("user_data");
    const passwordChangeRequired = localStorage.getItem(
      "requires_password_change",
    );

    if (token && userData) {
      setUser(JSON.parse(userData));
      setRequiresPasswordChange(passwordChangeRequired === "true");
    }

    // Set up the token expiry callback
    apiService.setTokenExpiryCallback(handleTokenExpiry);

    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await apiService.login(credentials);

      // Handle password reset required (legacy bcrypt hash migration)
      if (response.requiresPasswordReset) {
        return {
          requiresPasswordReset: true,
          message: response.message,
          email: response.email,
        };
      }

      // Handle club selection requirement
      if (response.requiresClubSelection) {
        return {
          requiresClubSelection: true,
          user: response.user,
          clubs: response.clubs,
        };
      }

      // Normal login flow
      localStorage.setItem("auth_token", response.token);
      localStorage.setItem("user_data", JSON.stringify(response.user));
      localStorage.setItem(
        "requires_password_change",
        response.requiresPasswordChange.toString(),
      );

      setUser(response.user);
      setRequiresPasswordChange(response.requiresPasswordChange);

      if (response.requiresPasswordChange) {
        toast.success(
          "Login successful! Please change your temporary password.",
        );
      } else {
        toast.success(`Welcome to ${response.user.club_name || "Eval Ninja"}!`);
      }

      return {
        success: true,
        requiresPasswordChange: response.requiresPasswordChange,
        user: response.user,
      };
    } catch (error) {
      toast.error(error.message || "Login failed. Please check your credentials.");
      return { success: false, error: error.message };
    }
  };

  const loginWithCode = async (email, accessCode) => {
    try {
      const response = await apiService.loginWithCode(email, accessCode);

      localStorage.setItem("auth_token", response.token);
      localStorage.setItem("user_data", JSON.stringify(response.user));
      localStorage.setItem("requires_password_change", "false");

      setUser(response.user);
      setRequiresPasswordChange(false);

      toast.success(`Welcome to ${response.user.club_name || "Eval Ninja"}!`);
      return { success: true, user: response.user };
    } catch (error) {
      toast.error(
        error.message || "Login failed. Please check your email and access code.",
      );
      return { success: false, error: error.message };
    }
  };

  const resetPassword = async (email) => {
    try {
      const response = await apiService.resetPassword(email);
      toast.success("A temporary password has been sent to your email.");
      return { success: true, message: response.message };
    } catch (error) {
      toast.error("Failed to reset password. Please try again.");
      return { success: false, error: error.message };
    }
  };

  const switchClub = async (clubId) => {
    try {
      const response = await apiService.switchClub(clubId);

      // Update token
      localStorage.setItem("auth_token", response.token);

      // Update user data with new club info
      const updatedUser = {
        ...user,
        club_id: response.club.id,
        club_name: response.club.name,
        role: response.club.role,
      };

      localStorage.setItem("user_data", JSON.stringify(updatedUser));
      setUser(updatedUser);

      toast.success(`Switched to ${response.club.name}`);

      return { success: true, club: response.club, user: updatedUser };
    } catch (error) {
      toast.error("Failed to switch clubs");
      return { success: false, error: error.message };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      await apiService.changePassword(passwordData);

      // Clear the password change requirement
      setRequiresPasswordChange(false);
      localStorage.setItem("requires_password_change", "false");

      toast.success("Password changed successfully!");
      return { success: true };
    } catch (error) {
      toast.error(error.message || "Failed to change password");
      return { success: false, error: error.message };
    }
  };

  const forceChangePassword = async (passwordData) => {
    try {
      await apiService.forceChangePassword(passwordData);

      // Clear the password change requirement
      setRequiresPasswordChange(false);
      localStorage.setItem("requires_password_change", "false");

      toast.success("Password set successfully!");
      return { success: true };
    } catch (error) {
      toast.error(error.message || "Failed to set password");
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("requires_password_change");
    setUser(null);
    setRequiresPasswordChange(false);
    toast.success("Logged out successfully");
  };

  const getRedirectPath = (user, intendedPath = null) => {
    // If there's an intended path, check if user has access to it
    if (intendedPath) {
      // Super admin can access any path
      if (user.role === "super_admin") {
        return intendedPath;
      }

      // Extract club ID from path if it's a club route
      const clubMatch = intendedPath.match(/^\/portal\/club\/([^/]+)/);
      if (clubMatch) {
        const intendedClubId = clubMatch[1];
        // Check if user has access to this club
        if (user.club_id === intendedClubId) {
          return intendedPath;
        } else {
          // User doesn't have access to this club, redirect to their own club
          if (user.club_id) {
            return `/portal/club/${user.club_id}/dashboard`;
          }
        }
      }

      // For evaluation routes, allow access for coaches and admins
      if (intendedPath.includes("/evaluation/")) {
        // Allow access for club_admin, coach, and super_admin
        if (["club_admin", "coach", "super_admin", "coordinator"].includes(user.role)) {
          return intendedPath;
        } else {
          // Regular evaluators don't have access to results pages
          if (user.club_id) {
            return `/portal/club/${user.club_id}/dashboard`;
          }
          return "/portal/login";
        }
      }

      // For non-club routes, check if it's an admin route
      if (intendedPath.startsWith("/admin/") && user.role !== "super_admin") {
        // Non-super admin trying to access admin routes
        if (user.club_id) {
          return `/portal/club/${user.club_id}/dashboard`;
        }
        return "/portal/login";
      }
    }

    // Default redirect based on user role
    if (user.role === "super_admin") {
      return "/admin/dashboard";
    }
    if (user.role === "coordinator" && user.club_id) {
      return `/portal/club/${user.club_id}/evaluations`;
    }
    if (user.club_id) {
      return `/portal/club/${user.club_id}/dashboard`;
    }

    return "/portal/login";
  };

  const value = {
    user,
    loading,
    requiresPasswordChange,
    login,
    loginWithCode,
    logout,
    switchClub,
    changePassword,
    forceChangePassword,
    resetPassword,
    getRedirectPath,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
